import { useContext, useEffect } from 'react';
import { Checkbox, MenuItem, Paper } from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import MuiRadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';

import { Loader } from 'BMapsSrc/Loader';
import Select from 'BMapsSrc/ui/common/Select';
import { useSelector } from 'react-redux';
import { ProteinContext } from '../../../../common/ProteinContextProvider';

/**
 * @typedef {"individual" | "selectivity" | "multiProtein"} selectivity
 */

/**
 * @param {{
 *      selectivity: selectivity
 *      target: 'target' | 'offtarget'
 *      setSelectivity: import('react').Dispatch<import('react').SetStateAction<selectivity>>
 *      viewOnly: boolean
 *      setTarget: import('react').Dispatch<import('react').SetStateAction<'target' | 'offtarget'>>
 * }} param0
 * @returns {import('react').ReactElement}
 */
export default function SelectivityControl({
    selectivity, setSelectivity, viewOnly,
    connectionWarning, target, setTarget,
    continueLoading, setContinueLoading,
}) {
    const { loadedProteins } = useContext(ProteinContext);
    const { targetList } = useSelector((state) => state.projectState);
    const isMultipleProteins = loadedProteins.length > 0;
    const displaySelectivityControl = isMultipleProteins || targetList.length > 0;

    const selectivityControlOpen = selectivity !== 'individual';

    const loadOptions = [
        {
            value: 'selectivity', label: 'Add for selectivity', title: 'Add an additional structure with resources for selectivity analysis', available: !viewOnly && isMultipleProteins,
        },
        {
            value: 'viewonly', label: 'Add view only structure', title: 'Add an additional structure for visualization only', available: viewOnly && isMultipleProteins,
        },
        {
            value: 'multiProtein',
            label: isMultipleProteins ? 'Add using multi protein support' : 'Add structure to workspace',
            title: isMultipleProteins ? 'Add an additional structure using single connection' : 'Add structure to workspace',
            available: Loader.AllowLabFeatures,
        },
        {
            value: 'proteinOfInterest', label: 'Add as a structure of interest', title: 'Add as a structure of interest', available: true,
        },
    ];
    const targetOptions = [
        {
            value: 'target', label: 'On target', title: 'Load as a target protein', available: true,
        },
        {
            value: 'offtarget', label: 'Off target', title: 'Load as an off target protein', available: true,
        },
    ];

    useEffect(() => {
        const options = loadOptions.filter((opt) => opt.available).map((opt) => opt.value);
        if (!options.includes(selectivity)) setSelectivity('individual');
    }, [loadedProteins]);

    if (!displaySelectivityControl) {
        setSelectivity('individual');
        setContinueLoading(false);
        setTarget('target');
        return null;
    }
    return (
        <Paper sx={{ padding: '1rem', height: 'unset !important', marginBottom: '0.5rem' }}>
            <MuiRadioGroup
                onChange={(evt) => {
                    if (evt.target.value === 'true') {
                        const options = loadOptions
                            .filter((opt) => opt.available)
                            .map((opt) => opt.value);
                        setSelectivity(options[0]);
                    } else {
                        setSelectivity('individual');
                        setContinueLoading(false);
                        setTarget('target');
                    }
                }}
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    marginBottom: 0,
                    marginLeft: '0.75rem',
                }}
            >
                <FormControlLabel
                    value="false"
                    checked={!selectivityControlOpen}
                    label="Clear workspace and load new structure"
                    control={<Radio size="small" />}
                />
                <FormControlLabel
                    value="true"
                    checked={selectivityControlOpen}
                    label="Add new structure for selectivity"
                    control={<Radio size="small" />}
                />
            </MuiRadioGroup>
            {selectivityControlOpen && (
            <>
                { connectionWarning && (
                <span style={{ fontStyle: 'italic', color: 'red', fontSize: 'smaller' }}>{connectionWarning}</span>
                )}
                <Select
                    size="small"
                    label="Selectivity Add type"
                    value={selectivity}
                    SelectProps={{
                        native: false,
                    }}
                    sx={{ margin: '1.5rem 0 1rem 0' }}
                    onChange={(evt) => setSelectivity(evt.target.value)}
                >
                    {loadOptions
                        .filter((option) => option.available)
                        .map((option) => (
                            <MenuItem
                                key={option.value}
                                value={option.value}
                            >
                                {option.label}
                            </MenuItem>
                        ))}
                </Select>
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <MuiRadioGroup
                        onChange={(evt) => setTarget(evt.target.value)}
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            marginBottom: 0,
                            marginLeft: '0.75rem',
                        }}
                    >
                        {targetOptions
                            .filter((option) => option.available)
                            .map((option) => (
                                <FormControlLabel
                                    key={option.value}
                                    value={option.value}
                                    checked={target===option.value}
                                    label={option.label}
                                    title={option.title}
                                    control={<Radio size="small" />}
                                />
                            ))}
                    </MuiRadioGroup>
                    <FormControlLabel
                        control={(
                            <Checkbox
                                checked={continueLoading}
                                onChange={(evt) => setContinueLoading(evt.target.checked)}
                            />
                    )}
                        label="Add another structure"
                    />
                </span>
            </>
            )}
        </Paper>
    );
}
